import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import {
  User as FirebaseUser,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { api } from '../api/client';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  error: string | null;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  createAccountWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function getAuthErrorMessage(err: unknown): string {
  const code = (err as { code?: string })?.code;
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Invalid email or password.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/popup-blocked':
      return 'Sign-in popup was blocked. Please allow popups and try again.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled.';
    default:
      return err instanceof Error ? err.message : 'An error occurred. Please try again.';
  }
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const verifyAndFetchUser = useCallback(async (_fbUser: FirebaseUser) => {
    try {
      // Backend returns flat UserResponse with camelCase fields
      interface BackendUserResponse {
        id: string;
        email: string | null;
        displayName: string | null;
        photoUrl: string | null;
        creditBalance: number;
        rssFeedToken: string | null;
        createdAt: string;
        isAdmin?: boolean;
      }
      const response = await api.post<BackendUserResponse>('/v1/auth/verify');
      setUser({
        id: response.id,
        uid: response.id,
        email: response.email || '',
        displayName: response.displayName || '',
        photoUrl: response.photoUrl,
        creditBalance: response.creditBalance,
        rssFeedToken: response.rssFeedToken || '',
        createdAt: new Date(response.createdAt),
        isAdmin: response.isAdmin === true,
      });
      setError(null);
    } catch (err) {
      console.error('Failed to verify user with backend:', err);
      setError('Failed to authenticate with server');
      setUser(null);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    if (!firebaseUser) return;
    await verifyAndFetchUser(firebaseUser);
  }, [firebaseUser, verifyAndFetchUser]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);

      if (fbUser) {
        await verifyAndFetchUser(fbUser);
      } else {
        setUser(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [verifyAndFetchUser]);

  const signInWithGoogle = useCallback(async () => {
    setError(null);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await verifyAndFetchUser(result.user);
    } catch (err) {
      setError(getAuthErrorMessage(err));
      throw err;
    }
  }, [verifyAndFetchUser]);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    setError(null);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      await verifyAndFetchUser(result.user);
    } catch (err) {
      setError(getAuthErrorMessage(err));
      throw err;
    }
  }, [verifyAndFetchUser]);

  const createAccountWithEmail = useCallback(async (email: string, password: string) => {
    setError(null);
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await verifyAndFetchUser(result.user);
    } catch (err) {
      setError(getAuthErrorMessage(err));
      throw err;
    }
  }, [verifyAndFetchUser]);

  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setFirebaseUser(null);
      setError(null);
    } catch (err) {
      setError(getAuthErrorMessage(err));
      throw err;
    }
  }, []);

  const value: AuthContextType = {
    user,
    firebaseUser,
    loading,
    error,
    signInWithGoogle,
    signInWithEmail,
    createAccountWithEmail,
    signOut,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
